import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { applyMask } from '../utils/masks';
import axios from 'axios';
import { lerValidade, validadeVencida } from '../utils/validade-cartao';
import { capturePaymentError, captureNetworkError } from '../services/error-tracking.service';
import { sendPurchaseToN8n } from '../utils/n8n-webhook';
import { getMetaClickIds } from '../utils/metaClickIds';
import { trackMetaPurchase } from '../utils/metaPurchase';
import { isInApp, postToApp } from '../utils/inApp';
import type { TermosAssinatura } from '../utils/termos-assinatura';
import { PrecoDoRodape, QuadroDeTermos } from './TermosAssinatura';

interface PaymentsCardProps {
  clientId: string;
  planId: string;
  coupon: {
    code: string;
    discount_type: 'PERCENT' | 'AMOUNT';
    value: number;
  } | null;
  price: {
    total: string;
    cupom: boolean;
    /** Linha pequena sob o total na barra do celular (ex.: "por mês · 12x no cartão"). */
    detalhe?: string;
  };
  /**
   * Termos da assinatura (utils/termos-assinatura): quadro acima do botão de
   * pagar e o valor/frequência da barra do celular, no lugar de total/detalhe.
   * Só exibição — `price.total` segue alimentando purchase/GTM/n8n.
   */
  termos?: TermosAssinatura;
  // Refactor client→sub: IDs dos children dessa sessão de checkout
  childIds?: string[];
  // Refactor client→sub: dados do pagador/endereço dessa sessão
  // (formData do checkout — passa pra backend popular AppMax customer + sub snapshot)
  recipient?: {
    name?: string;
    cpf?: string;
    whatsapp?: string;
  };
  shipping?: {
    cep?: string;
    rua?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    recebedor?: string;
  };
  email: string;
  onEmailChange: (value: string) => void;
  ensureEmailSaved: (email: string) => Promise<string>;
  // Renovação self-service (/renovar/:token). Ausente = compra normal do checkout.
  renewal?: {
    token: string;
    turnstileToken?: string;
    requireTurnstile?: boolean;
    // 'upgrade' só muda a copy de sucesso; o contrato é o mesmo token.
    purpose?: 'renewal' | 'upgrade';
  };
}

// 🔑 Função para gerar UUID v4 (idempotency key)
const generateIdempotencyKey = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Helper para parsear preço formatado "R$ 69,90" -> 69.90
const parseFormattedPrice = (priceStr: string): number => {
  return parseFloat(priceStr.replace(/[^\d,]/g, '').replace(',', '.'));
};

// Validação simples de e-mail (inline no checkout)
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const PaymentsCards: React.FC<PaymentsCardProps> = ({ clientId: clientIdProp, planId, coupon, price, childIds, recipient, shipping, email, onEmailChange, ensureEmailSaved, renewal, termos }) => {
  const [utmData, setUtmData] = useState({
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    utmTerm: '',
    utmContent: '',
  });

  const [form, setForm] = useState({
    cardNumber: '',
    validity: '',
    cvv: '',
    nameOnCard: '',
    cpf: '',
  });

  const [cardBrandError, setCardBrandError] = useState<string | null>(null);
  const [cpfError, setCpfError] = useState<string | null>(null);
  const [validadeSaiu, setValidadeSaiu] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);

  // 🔒 PROTEÇÃO CONTRA MÚLTIPLOS CLIQUES COM LOCALSTORAGE
  const [isProcessing, setIsProcessing] = useState(() => {
    // Verifica se há um processamento pendente no localStorage
    const stored = localStorage.getItem('payment_processing_card');
    if (stored) {
      const { timestamp } = JSON.parse(stored);
      const now = Date.now();
      // Se passou mais de 2 minutos, considera expirado
      if (now - timestamp < 120000) {
        return true; // Ainda processando
      }
      // Se expirou, remove do localStorage
      localStorage.removeItem('payment_processing_card');
    }
    return false;
  });

  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Timeout de segurança
  const formRenderedAtRef = useRef<number>(Date.now()); // ⏱️ Anti-bot: timestamp de quando o formulário apareceu
  const MIN_FILL_TIME = 5000; // 5 segundos — nenhum humano preenche cartão em menos
  const [paymentResult, setPaymentResult] = useState<{
    status: 'aprovado' | 'recusado' | 'pendente' | 'erro' | 'duplicado';
    message: string;
    /** `reason` estável do backend (renovação e recusa de cartão). */
    reason?: string;
  } | null>(null);

  // {orderId, nonce} da criação do pagamento com source='app' — troca por token
  // de auto-login quando aprovar (Task seguinte). Só em memória (padrão do checkout).
  const [appSession, setAppSession] = useState<{ orderId: string; nonce: string } | null>(null);

  // Disparo único: polling e WebSocket podem confirmar de novo antes do app
  // fechar a WebView — o postMessage não pode duplicar.
  const completedRef = useRef(false);

  // Aprovado dentro do app: troca o nonce pelo token de auto-login e devolve o
  // controle pro app via postMessage. Sem redirect WordPress — o container é o app.
  // Falha na troca NÃO bloqueia: postMessage sem token → app orienta primeiro acesso.
  const completeInApp = async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    let loginToken: string | undefined;
    try {
      if (appSession) {
        const res = await api.post('/payments/app-login-token', appSession);
        loginToken = (res.data?.data ?? res.data)?.token;
      }
    } catch (err) {
      // sem token → app cai no primeiro acesso JIT
      captureNetworkError(err, '/payments/app-login-token', 'POST', {
        component: 'PaymentsCard',
        action: 'app_login_token_exchange',
      });
    }
    postToApp({ type: 'purchase-complete', loginToken, email });
  };

  useEffect(() => {
    // Só scrolla pro topo quando aprovado (overlay cobre tudo)
    // Em erro/recusa, mantém posição pra usuário ver a mensagem
    if (paymentResult?.status === 'aprovado') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [paymentResult]);

  // ✅ Valida CPF (verifica dígitos verificadores)
  const validateCPF = (cpf: string): boolean => {
    const cleanCPF = cpf.replace(/\D/g, '');

    // CPF deve ter 11 dígitos
    if (cleanCPF.length !== 11) {
      return false;
    }

    // Elimina CPFs conhecidos como inválidos (todos dígitos iguais)
    if (/^(\d)\1{10}$/.test(cleanCPF)) {
      return false;
    }

    // Valida primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
    }
    let remainder = 11 - (sum % 11);
    const firstDigit = remainder >= 10 ? 0 : remainder;

    if (firstDigit !== parseInt(cleanCPF.charAt(9))) {
      return false;
    }

    // Valida segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
    }
    remainder = 11 - (sum % 11);
    const secondDigit = remainder >= 10 ? 0 : remainder;

    if (secondDigit !== parseInt(cleanCPF.charAt(10))) {
      return false;
    }

    return true;
  };

  // 🔍 Detecta bandeira do cartão pelo BIN (primeiros 6 dígitos)
  const detectCardBrand = (cardNumber: string): string | null => {
    const bin = cardNumber.replace(/\s/g, '').substring(0, 6);

    // American Express: começa com 34 ou 37
    if (/^3[47]/.test(bin)) {
      return 'amex';
    }

    // Visa: começa com 4
    if (/^4/.test(bin)) {
      return 'visa';
    }

    // Mastercard: começa com 51-55 ou 2221-2720
    if (/^5[1-5]/.test(bin) || /^2[2-7]/.test(bin)) {
      return 'mastercard';
    }

    // Elo: vários BINs
    if (/^(4011|4312|4389|4514|4576|5041|5066|5090|6277|6362|6363|6504|6505|6516|6550)/.test(bin)) {
      return 'elo';
    }

    return null;
  };

  const handleChange = (field: string, value: string) => {
    let masked = value;
    if (field === 'cardNumber') masked = applyMask(value, 'card');
    else if (field === 'cvv') masked = applyMask(value, 'cvv');
    else if (field === 'validity') masked = applyMask(value, 'validity');
    else if (field === 'cpf') masked = applyMask(value, 'cpf');

    setForm((prev) => ({ ...prev, [field]: masked }));

    // Valida bandeira do cartão quando número é alterado
    if (field === 'cardNumber') {
      const cleanNumber = masked.replace(/\s/g, '');

      if (cleanNumber.length >= 2) {
        const brand = detectCardBrand(cleanNumber);

        if (brand === 'amex') {
          setCardBrandError(
            '⚠️ Desculpe, cartões American Express não são aceitos no momento. ' +
              'Por favor, utilize Visa, Mastercard ou Elo.',
          );
        } else {
          setCardBrandError(null);
        }
      } else {
        setCardBrandError(null);
      }
    }

    // Valida CPF quando campo é alterado
    if (field === 'cpf') {
      const cleanCPF = masked.replace(/\D/g, '');

      // Só valida se tiver 11 dígitos (CPF completo)
      if (cleanCPF.length === 11) {
        if (!validateCPF(masked)) {
          setCpfError('⚠️ CPF inválido. Por favor, verifique os números digitados.');
        } else {
          setCpfError(null);
        }
      } else if (cleanCPF.length > 0 && cleanCPF.length < 11) {
        // CPF incompleto
        setCpfError(null); // Não mostra erro enquanto está digitando
      } else {
        setCpfError(null);
      }
    }
  };

  // Campo digitado (MM/AA ou MM/AAAA). Era um seletor de calendário que apagava
  // o que se digitava fora de MM/AAAA — no celular o teclado abre e a pessoa
  // digita a validade como vem no cartão (05/28): campo vazio, botão cinza sem aviso.
  const validade = lerValidade(form.validity);
  const validadeVencidaNoForm = validade !== null && validadeVencida(validade);
  // Aviso só ao sair do campo ou com MM/AAAA completo: no meio de 05/2028 o
  // campo passa por 05/20, que lido como MM/AA é um cartão vencido.
  const validadeComErro =
    (validadeSaiu || form.validity.length === 7) &&
    form.validity !== '' &&
    (validade === null || validadeVencidaNoForm);

  const isFormValid =
    form.cardNumber.replace(/[^0-9]/g, '').length === 16 && // Exige exatamente 16 dígitos (padrão Appmax)
    validade !== null &&
    !validadeVencidaNoForm &&
    form.cvv.replace(/[^0-9]/g, '').length >= 3 &&
    form.nameOnCard.trim().length > 3 &&
    form.cpf.replace(/[^0-9]/g, '').length === 11 &&
    isValidEmail(email) && // E-mail válido obrigatório
    !cardBrandError && // Bloqueia se houver erro de bandeira (Amex)
    !cpfError && // Bloqueia se CPF for inválido
    // Renovação de descoberta-mensal: o guard do backend exige Turnstile e o
    // fluxo do link não passa pelo POST /clients, então a verificação é feita aqui.
    !(renewal?.requireTurnstile && !renewal.turnstileToken);

  const createCardToken = async () => {
    // 🔒 PROTEÇÃO CONTRA MÚLTIPLOS CLIQUES
    if (isProcessing) {
      console.warn('⚠️ Pagamento já está sendo processado. Ignorando clique duplicado.');
      return;
    }

    // O botão já fica desabilitado sem validade; aqui só garante antes de travar o loading.
    const validadeLida = lerValidade(form.validity);
    if (!validadeLida) return;

    // ⏱️ ANTI-BOT: rejeitar se formulário preenchido muito rápido
    const fillTime = Date.now() - formRenderedAtRef.current;
    if (fillTime < MIN_FILL_TIME) {
      console.warn('🤖 Preenchimento muito rápido:', fillTime, 'ms');
      setPaymentResult({
        status: 'erro',
        message: 'Por favor, aguarde um momento antes de finalizar.',
      });
      return;
    }

    // Salva o e-mail no cliente (com dedup) antes de cobrar e usa o clientId resolvido
    if (!isValidEmail(email)) {
      setEmailError('Digite um e-mail válido para receber as informações.');
      return;
    }
    // Na renovação o dono já é conhecido (vem do token) e o e-mail é só leitura:
    // salvar de novo poderia trocar o clientId em voo pelo dedup e derrubar a
    // validação do link no backend.
    let clientId = clientIdProp;
    if (!renewal) {
      try {
        clientId = await ensureEmailSaved(email);
      } catch (saveError: unknown) {
        // 409 (#362): cadastro com assinatura não troca o e-mail pelo checkout — a
        // mensagem do backend diz o que fazer; "tente novamente" só daria o mesmo erro.
        const axiosError = saveError as import('axios').AxiosError<{ message?: string }>;
        setEmailError(
          axiosError?.response?.status === 409 && axiosError.response.data?.message
            ? axiosError.response.data.message
            : 'Não foi possível salvar o e-mail. Tente novamente.',
        );
        return;
      }
    }
    setEmailError(null);

    setLoading(true);
    setIsProcessing(true);

    // 🔑 Idempotency key gerada pelo frontend para rastreamento
    // A proteção real é no backend: mesma chave para mesmo clientId+planId+cpf
    const idempotencyKey = generateIdempotencyKey();

    // 💾 Salva estado no localStorage com idempotency key
    localStorage.setItem(
      'payment_processing_card',
      JSON.stringify({
        timestamp: Date.now(),
        idempotencyKey,
        clientId,
        planId,
      }),
    );

    // 🔒 Timeout de segurança: Se após 2 minutos ainda estiver processando, libera
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }
    processingTimeoutRef.current = setTimeout(() => {
      console.warn('⚠️ Timeout de segurança: liberando processamento após 2 minutos');
      setIsProcessing(false);
      localStorage.removeItem('payment_processing_card'); // Limpa localStorage
    }, 120000); // 2 minutos

    const number = form.cardNumber.replace(/\s/g, '');
    const cvv = form.cvv;
    const { month: parsedMonth, year: parsedYear } = validadeLida;
    const month = String(parsedMonth).padStart(2, '0');
    const name = form.nameOnCard;
    const cpf = form.cpf.replace(/\D/g, '');

    try {
      // Tokenização do cartão
      let token;
      try {
        const tokenResponse = await api.post('/payments/tokenize', {
          name,
          number,
          cvv,
          month: parsedMonth,
          year: parsedYear,
          clientId,
        });
        token = tokenResponse.data.data.token;
      } catch (tokenError: unknown) {
        // Captura erro de tokenização
        const axiosError = tokenError as import('axios').AxiosError<{ message?: string }>;
        const errorMessage = axiosError?.response?.data?.message;
        capturePaymentError(tokenError, 'card', {
          component: 'PaymentsCard',
          action: 'payment_card_failed',
          clientId,
          planId,
          additionalInfo: {
            cardBin: number.substring(0, 6), // Primeiros 6 dígitos (BIN)
            cardLast4: number.slice(-4), // Últimos 4 dígitos
            cardLength: number.length, // Comprimento do número (ajuda a identificar AmEx ou cartões inválidos)
            errorMessage,
          },
        });

        // Mensagem mais amigável para o usuário
        setPaymentResult({
          status: 'erro',
          message: errorMessage || 'Não foi possível processar este cartão. Verifique os dados digitados ou tente outro cartão.',
        });

        setLoading(false);
        setIsProcessing(false);
        localStorage.removeItem('payment_processing_card');
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }
        return; // Evita continuar o processamento
      }

      // IP do comprador: resolvido no backend a partir do request (#133) —
      // antes vinha do api.ipify.org, terceiro no caminho crítico da compra.
      const response = await api.post('/payments', {
        token,
        cpf,
        clientId,
        coupon,
        cvv,
        month: month, // Envia como string (ex: "11")
        year: String(parsedYear % 100).padStart(2, '0'), // Envia como string com 2 dígitos (ex: "29")
        planId,
        idempotencyKey, // 🔑 Envia idempotency key para backend
        childIds, // Refactor: vincula esses children à sub criada via webhook
        // Refactor: AppMax customer + order snapshot usam dados do form atual
        // (mesclamos CPF local — coletado neste componente)
        recipient: { ...recipient, cpf },
        shipping,
        ...utmData,
        ...(renewal && {
          renewalToken: renewal.token,
          ...(renewal.turnstileToken && { turnstileToken: renewal.turnstileToken }),
        }),
        ...(isInApp() && { source: 'app' }),
        ...getMetaClickIds(), // #389: fbc/fbp pro Purchase da CAPI
      });

      // NestJS encapsula resposta em { success, data: { payment, process } }
      const result = response.data?.data || response.data;

      // Presente só quando source='app' foi enviado — captura antes de setar
      // paymentResult pra já estar disponível quando o efeito de aprovado rodar.
      if (result?.appLoginNonce && result?.appOrderId) {
        setAppSession({ orderId: result.appOrderId, nonce: result.appLoginNonce });
      }

      if (result.process?.success === true) {
        // Pré-autorização e aprovação tratados como sucesso.
        // 99% das pré-autorizações são aprovadas pela antifraude.
        // O 1% estornado é tratado internamente pela equipe.
        setPaymentResult({
          status: 'aprovado',
          message: 'Pagamento aprovado com sucesso!',
        });
        // #391: Purchase do pixel com o mesmo event_id da CAPI (a Meta deduplica).
        // Renovação não dispara (o servidor também não conta). Em análise
        // antifraude (`isPending`) também não: o servidor só manda na aprovação
        // do webhook, e o que a antifraude recusa virava Purchase só do navegador.
        if (!renewal && !result.process?.isPending) {
          trackMetaPurchase({
            appmaxOrderId: result.payment?.order?.appmaxOrderId,
            value: result.payment?.amount,
          });
        }

        // TikTok Pixel - Purchase
        try {
          if (window.ttq) {
            window.ttq.track('CompletePayment', {
              content_id: planId,
              content_name: result.planName || 'Plano',
              value: parseFormattedPrice(price.total),
              currency: 'BRL',
            });
          }
        } catch { /* benigno */ }

        // dataLayer - purchase (GTM)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: result.payment.id || result.orderId,
            currency: 'BRL',
            value: parseFormattedPrice(price.total),
            coupon: coupon?.code || '',
            items: [
              {
                item_id: planId,
                item_name: result.planName || 'Plano',
                price: parseFormattedPrice(price.total),
                quantity: 1,
              },
            ],
          },
        });

        // Envia cópia para n8n (fire-and-forget)
        console.log('[PaymentsCard] Enviando para n8n, planId:', planId);
        sendPurchaseToN8n({
          event: 'purchase',
          transaction_id: result.payment.id || result.orderId,
          value: parseFormattedPrice(price.total),
          currency: 'BRL',
          coupon: coupon?.code || '',
          items: [
            {
              item_id: planId,
              item_name: result.planName || 'Plano',
              price: parseFormattedPrice(price.total),
              quantity: 1,
            },
          ],
          payment_method: 'card',
          plan_id: planId,
          plan_name: window.location.pathname.split('/')[1],
        });
      } else {
        // Captura pagamento recusado como erro
        const recusedError = new Error(result.process.text || 'Pagamento recusado');
        capturePaymentError(recusedError, 'card', {
          component: 'PaymentsCard',
          action: 'payment_refused',
          clientId,
          planId,
          additionalInfo: {
            paymentStatus: result.payment.status,
            processText: result.process.text,
            processCode: result.process.code,
            cardBin: number.substring(0, 6),
            cardLast4: number.slice(-4),
            amount: price.total,
            coupon: coupon?.code,
          },
        });

        setPaymentResult({
          status: 'recusado',
          message: result.process.text || 'Pagamento recusado.',
        });
        // 🔒 Libera botão para nova tentativa em caso de recusa
        setIsProcessing(false);
        localStorage.removeItem('payment_processing_card');
      }
    } catch (error: unknown) {
      // 🔁 Compra duplicada bloqueada pelo backend (409 + isDuplicate): não é erro
      // de sistema — informa o cliente sem capturar como payment error.
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        (error.response.data as { isDuplicate?: boolean })?.isDuplicate
      ) {
        const dupData = error.response.data as { message?: string };
        setPaymentResult({
          status: 'duplicado',
          message:
            dupData.message ||
            'Você já tem uma compra deste plano em andamento.',
        });
        setIsProcessing(false);
        localStorage.removeItem('payment_processing_card');
        return;
      }

      let message = 'Erro ao processar pagamento. Tente novamente.';

      const axiosError = error as {
        response?: { data?: unknown; status?: number };
        message?: string;
        code?: string;
        config?: { timeout?: number; baseURL?: string };
      };

      // Captura o erro para enviar ao sistema de tracking
      capturePaymentError(error, 'card', {
        component: 'PaymentsCard',
        action: 'create_payment',
        clientId,
        planId,
        additionalInfo: {
          cardBin: number.substring(0, 6),
          cardLast4: number.slice(-4),
          cpf: cpf.replace(/\d/g, '*').slice(0, 3) + '***' + cpf.slice(-2), // Mascara CPF
          amount: price.total,
          coupon: coupon?.code,
          errorResponse: axiosError?.response?.data,
          errorStatus: axiosError?.response?.status,
          errorMessage: axiosError?.message,
        },
      });

      // Se for erro de rede, captura também como erro de rede
      if (axiosError?.code === 'ERR_NETWORK' || axiosError?.code === 'ECONNABORTED') {
        captureNetworkError(error, '/payments', 'POST', {
          clientId,
          planId,
          additionalInfo: {
            timeout: axiosError?.config?.timeout,
            baseURL: axiosError?.config?.baseURL,
          },
        });
      }

      // Trata erros específicos do backend
      let reason: string | undefined;
      if (axios.isAxiosError(error)) {
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            message = error.response.data;
          } else if (error.response.data.message) {
            message = error.response.data.message;
          }
          if (typeof error.response.data === 'object') {
            reason = (error.response.data as { reason?: string }).reason;
          }
        }
      }

      // 🔧 FIX: Verificar se é pré-autorização bem-sucedida (AppMax retorna como erro mas é sucesso)
      const isPreAuthSuccess =
        message.toLowerCase().includes('pre autorização') ||
        message.toLowerCase().includes('pré-autorização') ||
        message.toLowerCase().includes('pre-autorização') ||
        message.toLowerCase().includes('autorização realizada');

      if (isPreAuthSuccess) {
        // Pré-autorização bem-sucedida - tratar como pagamento aprovado
        console.log('[PaymentsCard] Pré-autorização detectada - tratando como sucesso');

        // Tenta extrair payment.id da resposta de erro (se disponível)
        const responseData = axiosError?.response?.data as { payment?: { id?: string } } | undefined;
        const transactionId = responseData?.payment?.id || 'pre-auth-' + Date.now();

        setPaymentResult({
          status: 'aprovado',
          message: message || 'Pagamento aprovado com sucesso!',
        });

        // #391: sem Purchase do pixel aqui — a resposta de erro não traz o id da
        // order (sem event_id não há dedup) e a aprovação ainda depende do webhook.

        // TikTok Pixel - Purchase (pré-autorização)
        try {
          if (window.ttq) {
            window.ttq.track('CompletePayment', {
              content_id: planId,
              content_name: 'Plano',
              value: parseFormattedPrice(price.total),
              currency: 'BRL',
            });
          }
        } catch { /* benigno */ }

        // dataLayer - purchase (GTM)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: transactionId,
            currency: 'BRL',
            value: parseFormattedPrice(price.total),
            coupon: coupon?.code || '',
            items: [
              {
                item_id: planId,
                item_name: 'Plano',
                price: parseFormattedPrice(price.total),
                quantity: 1,
              },
            ],
          },
        });

        // Envia cópia para n8n (fire-and-forget)
        console.log('[PaymentsCard] Enviando para n8n (pré-autorização), planId:', planId);
        sendPurchaseToN8n({
          event: 'purchase',
          transaction_id: transactionId,
          value: parseFormattedPrice(price.total),
          currency: 'BRL',
          coupon: coupon?.code || '',
          items: [
            {
              item_id: planId,
              item_name: 'Plano',
              price: parseFormattedPrice(price.total),
              quantity: 1,
            },
          ],
          payment_method: 'card',
          plan_id: planId,
          plan_name: window.location.pathname.split('/')[1],
        });

        return; // Sai do catch sem mostrar erro
      }

      // Cartão recusado pela operadora chega como 400 com reason próprio: é
      // recusa (tente outro cartão), não erro do sistema.
      setPaymentResult({
        status: reason === 'pagamento_recusado' ? 'recusado' : 'erro',
        message,
        reason,
      });
      // 🔒 Libera botão para nova tentativa em caso de erro
      setIsProcessing(false);
      localStorage.removeItem('payment_processing_card');
    } finally {
      setLoading(false);
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      // 🔒 NÃO libera isProcessing nem limpa localStorage aqui.
      // Liberação acontece apenas em caso de erro/recusa (já tratado nos blocos acima).
      // Se pagamento foi aprovado, botão fica bloqueado permanentemente.
    }
  };

  // 🔒 Cleanup: Limpar timeout ao desmontar componente
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setUtmData({
      utmSource: params.get('utm_source') || '',
      utmMedium: params.get('utm_medium') || '',
      utmCampaign: params.get('utm_campaign') || '',
      utmTerm: params.get('utm_term') || '',
      utmContent: params.get('utm_content') || '',
    });
  }, []);

  useEffect(() => {
    if (paymentResult?.status === 'aprovado') {
      // Compra nova in-app: devolve controle pro app via postMessage — sem redirect
      // WordPress (o container é o app). Renewal/upgrade in-app (WebView do app antigo,
      // sem appSession/nonce) mantém o caminho antigo — só o postMessage é novo aqui.
      if (isInApp() && !renewal) {
        void completeInApp();
        return;
      }

      // Redirect rápido — 2 segundos para o usuário ver a confirmação
      const timeout = setTimeout(() => {
        window.location.href = 'https://coracoespreciosos.com.br/compra-aprovada/';
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [paymentResult]);

  return (
    <>
      {/* 🎉 OVERLAY DE COMPRA APROVADA — bloqueia toda interação */}
      {paymentResult?.status === 'aprovado' && (
        <div className="fixed inset-0 z-[9999] bg-white flex items-center justify-center flex-col gap-4">
          <div className="text-green-500 text-6xl">✓</div>
          <h2 className="text-2xl font-bold text-gray-800">
            {renewal
              ? renewal.purpose === 'upgrade'
                ? 'Upgrade concluído!'
                : 'Assinatura renovada!'
              : 'Compra Aprovada!'}
          </h2>
          <p className="text-gray-600 text-center px-4">
            {paymentResult.message}
          </p>
          <p className="text-sm text-gray-400">
            {isInApp() && !renewal ? 'Voltando ao aplicativo...' : 'Redirecionando...'}
          </p>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mt-2"></div>
        </div>
      )}


      {/* Número do Cartão */}
      <div className={`py-3 flex flex-col w-full mt-5 gap-2 ${paymentResult?.status === 'aprovado' && 'hidden'}`}>
        <div className="relative flex items-center justify-between">
          <label className="text-base text-[#595D69]">Número do cartão</label>
          <img src="/cards.svg" alt="Bandeiras" className="absolute lg:hidden block top-1 right-1 h-5" />
        </div>
        <div className="relative w-full">
          <input
            className={`border w-full px-4 py-4 rounded-lg h-12 pr-28 ${
              cardBrandError ? 'border-red-500' : 'border-[#dadae5]'
            }`}
            placeholder="0000 0000 0000 0000"
            inputMode="numeric"
            maxLength={19}
            value={form.cardNumber}
            onChange={(e) => handleChange('cardNumber', e.target.value)}
          />
          <img src="/cards.svg" alt="Bandeiras" className="absolute hidden lg:flex top-2 right-4 h-8" />
        </div>
        {cardBrandError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 mt-1">
            {cardBrandError}
          </div>
        )}
      </div>

      {/* Validade e CVV */}
      <div className={`flex items-center justify-between gap-5 ${paymentResult?.status === 'aprovado' && 'hidden'}`}>
        {/* Validade */}
        <div className="relative flex-[2]">
          <div className="flex items-center justify-between">
            <label className="text-base text-[#595D69]">Validade</label>
            <button className="hover:cursor-pointer hover:opacity-90 hidden lg:flex  text-[#595D69] items-center gap-1">
              Ajuda <img src="/icon-question.svg" />
            </button>
          </div>
          <input
            className={`border mt-5 px-4 py-4 rounded-lg h-12 w-full ${
              validadeComErro ? 'border-red-500 bg-red-50' : 'border-[#dadae5]'
            }`}
            placeholder="MM/AA"
            inputMode="numeric"
            autoComplete="cc-exp"
            maxLength={7}
            value={form.validity}
            onChange={(e) => handleChange('validity', e.target.value)}
            onFocus={() => setValidadeSaiu(false)}
            onBlur={() => setValidadeSaiu(true)}
          />
          {validadeComErro && (
            <p className="absolute text-red-500 text-xs mt-1">
              {validadeVencidaNoForm ? 'Cartão vencido' : 'Validade inválida'}
            </p>
          )}
        </div>

        {/* CVV */}
        <div className="relative flex-1 flex flex-col">
          <div className="flex items-center justify-between">
            <label className="text-base text-[#595D69]">CVV</label>
            <button className="hover:cursor-pointer hover:opacity-90 hidden lg:flex  text-[#595D69] items-center gap-1">
              Ajuda <img src="/icon-question.svg" />
            </button>
          </div>
          <input
            className="border border-[#dadae5] mt-5 px-4 py-4 rounded-lg h-12 w-full"
            placeholder="000"
            inputMode="numeric"
            maxLength={4}
            value={form.cvv}
            onChange={(e) => handleChange('cvv', e.target.value)}
          />
          <img src="/card-cvv.svg" alt="" className="absolute hidden lg:flex top-[52px] right-4 h-6" />
        </div>
      </div>

      {/* Nome no cartão */}
      <div className={` py-3 flex flex-col w-full mt-5 gap-2 ${paymentResult?.status === 'aprovado' && 'hidden'}`}>
        <div className="flex items-center justify-between">
          <label className="text-base text-[#595D69]">Nome no cartão</label>
          <button className="hover:cursor-pointer hover:opacity-90 hidden lg:flex text-[#595D69] items-center gap-1">
            Ajuda <img src="/icon-question.svg" />
          </button>
        </div>
        <input
          name="nameOnCard"
          className="border border-[#dadae5] px-4 py-4 rounded-lg h-12"
          placeholder="Escreva igual ao impresso"
          value={form.nameOnCard}
          onChange={(e) => handleChange('nameOnCard', e.target.value)}
        />
      </div>

      {/* CPF */}
      <div className={` py-3 flex flex-col w-full mt-5 gap-2 ${paymentResult?.status === 'aprovado' && 'hidden'}`}>
        <label className="text-base text-[#595D69]">CPF do titular</label>
        <input
          className={`border px-4 py-4 rounded-lg h-12 ${cpfError ? 'border-red-500' : 'border-[#dadae5]'}`}
          placeholder="000.000.000-00"
          inputMode="numeric"
          maxLength={14}
          value={form.cpf}
          onChange={(e) => handleChange('cpf', e.target.value)}
        />
        {cpfError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 mt-1">{cpfError}</div>
        )}
      </div>

      {/* E-mail */}
      <div className={` py-3 flex flex-col w-full mt-5 gap-2 ${paymentResult?.status === 'aprovado' && 'hidden'}`}>
        <label className="text-base text-[#595D69]">E-mail</label>
        <span className="text-sm text-[#595D69]">
          {renewal
            ? `Enviaremos as informações da assinatura para este e-mail. Para trocá-lo, ${
                isInApp() ? 'use o aplicativo' : 'fale com a nossa equipe'
              }.`
            : 'Insira o email para receber as informações da assinatura'}
        </span>
        <input
          className={`border px-4 py-4 rounded-lg h-12 ${email && !isValidEmail(email) ? 'border-red-500 bg-red-50' : 'border-[#dadae5]'} ${renewal ? 'bg-gray-50 text-[#595D69]' : ''}`}
          placeholder="exemplo@email.com"
          type="email"
          inputMode="email"
          readOnly={!!renewal}
          value={email}
          onChange={(e) => {
            onEmailChange(e.target.value);
            setEmailError(null);
          }}
        />
        {email && !isValidEmail(email) && (
          <p className="text-red-500 text-sm">Digite um e-mail válido</p>
        )}
        {emailError && (
          <p className="text-red-500 text-sm">{emailError}</p>
        )}
      </div>

      {paymentResult && paymentResult.status === 'duplicado' && (
        <div className="text-sm mt-4 p-4 rounded-md bg-amber-50 text-amber-900 border border-amber-400">
          <p className="font-semibold mb-1">Compra já em andamento</p>
          <p>{paymentResult.message}</p>
          <p className="mt-2 text-amber-700">
            {renewal
              ? 'Não realizamos uma nova cobrança. Aguarde alguns minutos antes de tentar de novo.'
              : 'Não realizamos uma nova cobrança. Para assinar para outra criança, volte à etapa de crianças e adicione-a antes de pagar.'}
          </p>
        </div>
      )}

      {paymentResult &&
        paymentResult.status !== 'aprovado' &&
        paymentResult.status !== 'duplicado' && (
        <div className="text-sm mt-4 p-4 rounded-md bg-red-100 text-red-800 border border-red-400">
          <p className="font-semibold mb-1">
            {paymentResult.status === 'recusado' ? 'Pagamento recusado' : 'Erro no pagamento'}
          </p>
          <p>{paymentResult.message}</p>
          {paymentResult.status === 'recusado' && (
            <p className="mt-2 text-red-600">
              Verifique os dados do cartão ou tente outro cartão.
            </p>
          )}
          {renewal && paymentResult.status === 'erro' && (
            <p className="mt-2 text-red-600">
              {/* Link vencido/revogado só se resolve com link novo; o resto não. */}
              {paymentResult.reason?.startsWith('token_')
                ? isInApp()
                  ? 'Gere um novo link de renovação no aplicativo.'
                  : 'Peça um novo link de renovação na conversa do WhatsApp.'
                : 'Se o problema continuar, fale com a nossa equipe.'}
            </p>
          )}
        </div>
      )}

      {termos && paymentResult?.status !== 'aprovado' && <QuadroDeTermos termos={termos} />}

      {/* Botão Finalizar */}
      <button
        disabled={!isFormValid || loading || isProcessing}
        onClick={createCardToken}
        className={`text-white lg:flex mb-10 my-5 h-12 w-full px-3 py-2 rounded-md hidden items-center justify-center gap-2 ${
          isFormValid && !loading && !isProcessing
            ? 'bg-[#018ED5] hover:opacity-90 cursor-pointer'
            : 'bg-[#dadae5] cursor-not-allowed'
        }
        ${paymentResult?.status === 'aprovado' && 'hidden'}
        `}
      >
        {loading && (
          <svg
            className="animate-spin h-4 w-4 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        )}
        {loading ? 'Processando pagamento...' : 'Finalizar'}
      </button>

      <div className="w-full lg:hidden flex items-center justify-between gap-3 px-4 py-2 left-0 min-h-[80px] z-50 bg-white border-t border-black/10 fixed bottom-0">
        <div className="min-w-0 flex-1">
          <span>
            {price.cupom && (
              <>
                <img src="/icon-tag.svg" className="inline w-4 h-4" alt="" /> Cupom aplicado
              </>
            )}
          </span>
          {termos ? (
            <PrecoDoRodape rodape={termos.rodape} />
          ) : (
            <>
              <h3 className="font-bold text-3xl">{price.total}</h3>
              {price.detalhe && <p className="text-xs text-[#595D69]">{price.detalhe}</p>}
            </>
          )}
          <h2 className="font-bold text-xs">+ FRETE GRÁTIS</h2>
        </div>
        <div className="shrink-0 max-w-[50%]">
          <button
            className={`flex items-center justify-center gap-2 text-white px-4 py-4 text-center leading-tight rounded-md transition-all duration-300 ${
              isFormValid && !loading && !isProcessing
                ? 'bg-[#018ED5] hover:opacity-90'
                : 'bg-[#dadae5] cursor-not-allowed'
            }`}
            disabled={!isFormValid || loading || isProcessing}
            onClick={createCardToken}
          >
            {loading && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {loading ? 'Processando pagamento...' : 'Finalizar'}
          </button>
        </div>
      </div>
    </>
  );
};

export default PaymentsCards;
